import { useCallback, useEffect, useState } from 'react'
import { supabase, type Database } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { FluxoBlock } from '@/hooks/useFluxos'

export type ScheduledMessage = Database['public']['Tables']['account_scheduled_messages']['Row']
export type ScheduledMessageStatus = ScheduledMessage['status']
export type ScheduledKind = 'template' | 'fluxo'

type CreateScheduledBase = {
  account_contact_id: string
  remote_jid: string
  scheduled_at: string
  /** Default true — cancela se o contato responder antes do horário */
  cancel_on_reply?: boolean
}

export type CreateScheduledMessageInput =
  | (CreateScheduledBase & {
      kind: 'template'
      template_id: string
      content_snapshot: string
    })
  | (CreateScheduledBase & {
      kind: 'fluxo'
      fluxo_id: string
      blocks_snapshot: FluxoBlock[]
    })

export type ScheduledMutationResult = { ok: boolean; error?: string }

interface UseScheduledMessagesReturn {
  scheduledMessages: ScheduledMessage[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  createScheduledMessage: (input: CreateScheduledMessageInput) => Promise<ScheduledMessage | null>
  cancelScheduledMessage: (id: string) => Promise<ScheduledMutationResult>
  deleteScheduledMessage: (id: string) => Promise<ScheduledMutationResult>
  renderPreview: (content: string, contactId: string) => Promise<string>
}

type LocalChange =
  | { type: 'upsert'; message: ScheduledMessage }
  | { type: 'remove'; id: string }

/**
 * Espelha mutações entre instâncias do hook (modal de agendamento e painel do
 * lead abrem o mesmo contato em componentes separados). O realtime do Supabase
 * continua ativo, mas não podemos depender só dele para o feedback imediato.
 */
const changeListeners = new Map<string, Set<(change: LocalChange) => void>>()

function broadcastChange(contactId: string, change: LocalChange) {
  changeListeners.get(contactId)?.forEach((listener) => listener(change))
}

function subscribeToChanges(contactId: string, listener: (change: LocalChange) => void) {
  const listeners = changeListeners.get(contactId) ?? new Set()
  listeners.add(listener)
  changeListeners.set(contactId, listeners)

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) changeListeners.delete(contactId)
  }
}

const NO_ROWS_MESSAGE =
  'Nenhum registro foi alterado. Recarregue a página e tente novamente.'

export function useScheduledMessages(contactId: string | null | undefined): UseScheduledMessagesReturn {
  const { user } = useAuth()
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const applyUpsert = useCallback((message: ScheduledMessage) => {
    setScheduledMessages((prev) => {
      const exists = prev.some((m) => m.id === message.id)
      const next = exists
        ? prev.map((m) => (m.id === message.id ? message : m))
        : [...prev, message]
      return next.sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at))
    })
  }, [])

  const applyRemove = useCallback((id: string) => {
    setScheduledMessages((prev) => prev.filter((m) => m.id !== id))
  }, [])

  const fetchScheduled = useCallback(async () => {
    if (!supabase || !user?.account_id || !contactId) {
      setScheduledMessages([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const { data, error: fetchError } = await supabase
        .from('account_scheduled_messages')
        .select('*')
        .eq('account_id', user.account_id)
        .eq('account_contact_id', contactId)
        .order('scheduled_at', { ascending: true })

      if (fetchError) throw fetchError
      setScheduledMessages((data ?? []) as ScheduledMessage[])
    } catch (err: any) {
      console.error('[useScheduledMessages] fetch error:', err)
      setError(err?.message ?? 'Erro ao carregar mensagens agendadas')
    } finally {
      setLoading(false)
    }
  }, [contactId, user?.account_id])

  const createScheduledMessage = useCallback(
    async (input: CreateScheduledMessageInput): Promise<ScheduledMessage | null> => {
      if (!supabase || !user?.account_id) {
        setError('Supabase não configurado')
        return null
      }

      try {
        setError(null)

        const base = {
          account_id: user.account_id,
          account_contact_id: input.account_contact_id,
          remote_jid: input.remote_jid,
          scheduled_at: input.scheduled_at,
          status: 'pending' as const,
          created_by: user.id ?? null,
          source: 'manual' as const,
          cancel_on_reply: input.cancel_on_reply ?? true,
        }

        const row =
          input.kind === 'fluxo'
            ? {
                ...base,
                kind: 'fluxo' as const,
                fluxo_id: input.fluxo_id,
                blocks_snapshot: input.blocks_snapshot,
                template_id: null,
                content_snapshot: null,
              }
            : {
                ...base,
                kind: 'template' as const,
                template_id: input.template_id,
                content_snapshot: input.content_snapshot,
                fluxo_id: null,
                blocks_snapshot: null,
              }

        const { data, error: insertError } = await supabase
          .from('account_scheduled_messages')
          .insert(row)
          .select()
          .single()

        if (insertError) throw insertError

        const created = data as ScheduledMessage
        applyUpsert(created)
        broadcastChange(created.account_contact_id, { type: 'upsert', message: created })
        return created
      } catch (err: any) {
        console.error('[useScheduledMessages] create error:', err)
        setError(err?.message ?? 'Erro ao agendar mensagem')
        return null
      }
    },
    [applyUpsert, user]
  )

  const cancelScheduledMessage = useCallback(
    async (id: string): Promise<ScheduledMutationResult> => {
      if (!supabase) return { ok: false, error: 'Supabase não configurado' }
      try {
        setError(null)
        const { data, error: updateError } = await supabase
          .from('account_scheduled_messages')
          .update({ status: 'cancelled' })
          .eq('id', id)
          .eq('status', 'pending')
          .select()

        if (updateError) throw updateError

        const updated = (data ?? [])[0] as ScheduledMessage | undefined
        if (!updated) {
          setError(NO_ROWS_MESSAGE)
          return { ok: false, error: NO_ROWS_MESSAGE }
        }

        applyUpsert(updated)
        broadcastChange(updated.account_contact_id, { type: 'upsert', message: updated })
        return { ok: true }
      } catch (err: any) {
        console.error('[useScheduledMessages] cancel error:', err)
        const message = err?.message ?? 'Erro ao cancelar agendamento'
        setError(message)
        return { ok: false, error: message }
      }
    },
    [applyUpsert]
  )

  const deleteScheduledMessage = useCallback(
    async (id: string): Promise<ScheduledMutationResult> => {
      if (!supabase) return { ok: false, error: 'Supabase não configurado' }
      try {
        setError(null)
        const { data, error: deleteError } = await supabase
          .from('account_scheduled_messages')
          .delete()
          .eq('id', id)
          .select('id')

        if (deleteError) throw deleteError

        if (!data || data.length === 0) {
          setError(NO_ROWS_MESSAGE)
          return { ok: false, error: NO_ROWS_MESSAGE }
        }

        applyRemove(id)
        if (contactId) broadcastChange(contactId, { type: 'remove', id })
        return { ok: true }
      } catch (err: any) {
        console.error('[useScheduledMessages] delete error:', err)
        const message = err?.message ?? 'Erro ao remover agendamento'
        setError(message)
        return { ok: false, error: message }
      }
    },
    [applyRemove, contactId]
  )

  const renderPreview = useCallback(async (content: string, contactIdParam: string): Promise<string> => {
    if (!supabase) return content
    try {
      const { data, error: rpcError } = await supabase.rpc('render_scheduled_message', {
        p_content: content,
        p_contact_id: contactIdParam
      })
      if (rpcError) throw rpcError
      return (data as string) ?? content
    } catch (err: any) {
      console.error('[useScheduledMessages] preview error:', err)
      return content
    }
  }, [])

  useEffect(() => {
    if (!supabase || !user?.account_id || !contactId) return

    fetchScheduled()

    const channel = supabase
      .channel(`scheduled-msgs-${contactId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_scheduled_messages',
          filter: `account_contact_id=eq.${contactId}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            applyUpsert(payload.new as ScheduledMessage)
          } else if (payload.eventType === 'DELETE') {
            applyRemove((payload.old as ScheduledMessage).id)
          }
        }
      )
      .subscribe()

    return () => {
      supabase?.removeChannel(channel)
    }
  }, [applyRemove, applyUpsert, contactId, user?.account_id, fetchScheduled])

  useEffect(() => {
    if (!contactId) return

    return subscribeToChanges(contactId, (change) => {
      if (change.type === 'upsert') applyUpsert(change.message)
      else applyRemove(change.id)
    })
  }, [applyRemove, applyUpsert, contactId])

  return {
    scheduledMessages,
    loading,
    error,
    refetch: fetchScheduled,
    createScheduledMessage,
    cancelScheduledMessage,
    deleteScheduledMessage,
    renderPreview
  }
}
